import { t, tk } from "./i18n"

export const athensToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens" }).format(new Date())

/** The shop's own visitor counter, read by the HQ Worker (worker/analytics.ts). */
type Funnel = { visits: number; added: number; checkout: number; bought: number; revenue: number }
export type VisitorsReport = {
  from: string
  to: string
  totals: Funnel & {
    pageviews: number
    product_views: number
    units_added: number
    orders: number
    bounced: number
    add_to_cart_rate: number | null
    checkout_rate: number | null
    conversion_rate: number | null
    cart_to_order_rate: number | null
    bounce_rate: number | null
    pages_per_visit: number | null
  }
  by_day: Array<Funnel & { day: string }>
  by_source: Array<Funnel & { source: string; conversion_rate: number | null }>
  by_campaign: Array<Funnel & { campaign: string }>
  by_device: Array<Funnel & { device: string }>
  by_country: Array<Funnel & { country: string }>
  products: Array<{ product: string; views: number; viewers: number; adds: number; units: number; add_rate: number | null }>
  pages: Array<{ path: string; views: number }>
}

export async function fetchVisitors(from: string, to: string): Promise<VisitorsReport> {
  const response = await fetch(`/hq-analytics/visitors?from=${from}&to=${to}`, { credentials: "same-origin", headers: { accept: "application/json" } })
  const body = (await response.json().catch(() => ({}))) as VisitorsReport & { error?: string }
  if (!response.ok) throw new Error(body.error ?? `${response.status}`)
  return body
}

/** Where visits came from, in the owners' words. Brands are names in every language. */
const SOURCE: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  discord: "Discord",
  x: "X",
  google: "Google",
}
const SOURCE_WORDS: Record<string, string> = { direct: tk("Direct or unknown"), search: tk("Other search engines") }
export const sourceName = (source: string) => SOURCE[source] ?? (SOURCE_WORDS[source] ? t(SOURCE_WORDS[source]) : source)

const DEVICE: Record<string, string> = { mobile: tk("Phone"), tablet: tk("Tablet"), desktop: tk("Computer") }
export const deviceName = (device: string) => (DEVICE[device] ? t(DEVICE[device]) : device)

export function countryName(code: string, locale: string) {
  if (!/^[A-Z]{2}$/.test(code)) return t("Unknown")
  try {
    return new Intl.DisplayNames([locale === "el" ? "el" : "en"], { type: "region" }).of(code) ?? code
  } catch {
    return code
  }
}

/** A path as the owners know it: the page's name for the shop's own pages, else the path. */
const PAGES: Record<string, string> = {
  "/": tk("Home page"),
  "/collections/stock": tk("In stock now"),
  "/cart": tk("Cart"),
  "/checkout": tk("Checkout"),
  "/custom-orders": tk("Custom orders page"),
  "/reviews": tk("Reviews"),
  "/tracking": tk("Custom order tracking"),
  "/coverage": tk("Delivery check"),
  "/guide": tk("Guide"),
  "/policies": tk("Policies"),
  "/help": tk("Help"),
}
export const pageName = (path: string) => (PAGES[path] ? t(PAGES[path]) : path)
